import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IntegrationModel } from 'src/app/models/api';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { MenuAction } from 'src/app/models/menuAction.model';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import {
  ActionResponseModel,
  ActionsHttpServiceService,
  AgentStepActionsResponseModel,
  RequiredParamsForChatGptEnterpriseDeleteConversationAction,
} from '../../httpServices/actionsHttpService/actions-http-service.service';
import { PermissionsService } from '../../infra/permissionsService/permissions.service';
import { StoreService } from '../../infra/store/store.service';
import { CommonActionsService } from '../common/common-actions.service';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import _ from 'lodash';
import { AgentStepsSupportedActions } from './agent-steps-supported-actions';
import { AgentStepModel } from 'src/app/models/api/agentStep.model';
import { AiAgentStepActivityTableRowViewModel } from 'src/app/models/viewModels/aiAgentStepActivityTableRowViewModel';

@Injectable({
  providedIn: 'root',
})
export class AgentStepsActionsService {
  private _integrations: IntegrationModel[] = [];
  private readonly _actionIdActionModelDictionary: {
    [actionId: string]: AgentStepsSupportedActions;
  } = {
    chatGptEnterpriseDeleteConversationAction: AgentStepsSupportedActions.chatGptEnterpriseDeleteConversationAction,
  };

  constructor(
    private readonly _commonActionsService: CommonActionsService,
    private readonly _permissionsService: PermissionsService,
    private readonly _store: StoreService,
    private readonly _labelProvider: LabelsProviderPipe,
    private readonly _httpActionsServiceService: ActionsHttpServiceService
  ) {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      this._integrations = integrations;
    });
  }

  public async setActionsForAgentSteps(
    agentSteps: AgentStepModel[],
    agentStepsCurrentPage: AiAgentStepActivityTableRowViewModel[]
  ): Promise<void> {
    const agentStepsByIntegrationId = _.groupBy(agentSteps, 'integration.id');
    let flagCounter = 0;
    const maxFlagCounter = 100;
    Object.keys(agentStepsByIntegrationId).forEach((integrationId) => {
      this._httpActionsServiceService
        .getActionsOfAgentSteps({
          integrationId: integrationId,
          stepIds: agentStepsByIntegrationId[integrationId].map((agentStep) => agentStep.id),
        })
        .subscribe({
          next: (response) => {
            this._handleActionsResponse(integrationId, response, agentStepsCurrentPage);
            flagCounter = maxFlagCounter;
          },
        });
    });
    while (flagCounter < maxFlagCounter) {
      flagCounter++;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  public handleActionClicked(
    action: AgentStepsSupportedActions,
    resourceId: string,
    integrationId: string
  ): Observable<boolean> {
    const actionsHttpParams = {
      integrationId: integrationId,
      stepId: resourceId,
    } as RequiredParamsForChatGptEnterpriseDeleteConversationAction;

    if (action === AgentStepsSupportedActions.chatGptEnterpriseDeleteConversationAction) {
      return this._commonActionsService.chatGptEnterpriseDeleteGptConversationAction(actionsHttpParams);
    }

    throw new Error(`AgentStepsActionsService handleActionClicked: ${action} support not implemented.`);
  }

  private _handleActionsResponse(
    integrationId: string,
    response: JsonHalCollection<AgentStepActionsResponseModel>,
    agentStepsCurrentPage: AiAgentStepActivityTableRowViewModel[]
  ): void {
    const agentStepsActions = response._embedded.items;
    const agentStepsIdsActionsDict: {
      [stepId: string]: AgentStepActionsResponseModel;
    } = {};
    agentStepsActions.forEach((agentStepActions) => {
      agentStepsIdsActionsDict[agentStepActions.stepId] = agentStepActions;
    });
    agentStepsCurrentPage.forEach((agentStep) => {
      if (agentStep.step.integration.id == integrationId && agentStep.step.id in agentStepsIdsActionsDict) {
        agentStep.menuActions = this._getMenuActionForAgentStep(
          agentStep.step,
          agentStepsIdsActionsDict[agentStep.step.id].actions
        );
      }
    });
  }

  private _getMenuActionForAgentStep(agentStep: AgentStepModel, actions: ActionResponseModel[]): MenuAction[] {
    const newActions: MenuAction[] = [];
    actions.forEach((action) => {
      if (this._actionIdActionModelDictionary[action.id]) {
        const actionModel = this._actionIdActionModelDictionary[action.id];
        newActions.push(this._getMenuAction(actionModel));
      }
    });
    return newActions;
  }

  private _getMenuAction(supportedAction: AgentStepsSupportedActions): MenuAction {
    const hasPermissions = this._permissionsService.hasPermissions(PermissionEnum.operator);

    switch (supportedAction) {
      case AgentStepsSupportedActions.chatGptEnterpriseDeleteConversationAction:
        return {
          name: AgentStepsSupportedActions.chatGptEnterpriseDeleteConversationAction.toString(),
          displayNameLabel: 'commonActions.chatGptEnterpriseDeleteConversationAction',
          disabled: !hasPermissions,
          tooltip: !hasPermissions ? this._labelProvider.transform('commonActions.operatorActionTooltip') : '',
        } as MenuAction;
      default:
        throw new Error(`AgentStepsActionsService getMenuAction: The action ${supportedAction} is not supported`);
    }
  }
}
